import { useState, useEffect, useCallback, useRef } from "react";
import { getApiUrl } from "../../../../config/api";
import { getStableAccumulatedDataExtraQueryParams } from "../../../../services/dataService";
import { fetchStableAccumulatedJson } from "../../../../services/stableAccumulatedRequest";
import {
    computeSpotGammaGexMme,
    getUnderlyingPriceFallback,
} from "../../../../utils/gammaCalculations";
import {
    classifyGexStructure,
    classifyGammaTypeFromGexAndSpot,
    computeGammaLevels,
} from "../../../../utils/gammaChartUtils";
import type {
    GammaBar,
    GammaLevel,
    GEXMetrics,
    GEXProfileMode,
    GammaLookbackByStrike,
} from "../types";

const REFRESH_MS = 30_000;
const REQUEST_COOLDOWN_MS = 8_000;

type Snapshot = { ts: number; gexByStrike: Map<number, number> };

const EMPTY_METRICS: GEXMetrics = {
    updatedAt: Date.now(),
    spot: 0,
    zeroGamma: null,
    majorPositive: null,
    majorNegative: null,
    netGex: 0,
    maxChange1m: null,
    maxChange5m: null,
    maxChange15m: null,
    maxChange30m: null,
    nearestRepulsiveStrike: null,
    nearestMagneticStrike: null,
};

export function useGammaData(
    symbol: string,
    profileMode: GEXProfileMode = "full",
) {
    const [gammaData, setGammaData] = useState<GammaBar[]>([]);
    const [gammaLevels, setGammaLevels] = useState<GammaLevel[]>([]);
    const [lookbackByStrike, setLookbackByStrike] =
        useState<GammaLookbackByStrike>({});
    const [metrics, setMetrics] = useState<GEXMetrics>(EMPTY_METRICS);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval>>();
    const historyRef = useRef<Snapshot[]>([]);
    const lastRequestByKeyRef = useRef<Map<string, number>>(new Map());
    const inFlightKeysRef = useRef<Set<string>>(new Set());

    const fetch_ = useCallback(async () => {
        if (!symbol) return;
        const key = `${symbol.toUpperCase()}|${profileMode}`;
        if (inFlightKeysRef.current.has(key)) return;
        const now = Date.now();
        const lastRequest = lastRequestByKeyRef.current.get(key) || 0;
        if (now - lastRequest < REQUEST_COOLDOWN_MS) return;
        lastRequestByKeyRef.current.set(key, now);
        inFlightKeysRef.current.add(key);
        setLoading(true);
        try {
            let rawRows: any[] = [];
            // GEX chart needs full strike range — use strike_range=all and weekly expiry.
            // Do NOT use getStableAccumulatedDataExtraQueryParams() which reads user's data-table filters.
            const expiryRange =
                localStorage.getItem("mmec_expiry_range") || "weekly";
            const gexQs = `strike_range=all&expiry_range=${encodeURIComponent(expiryRange)}`;
            // Stable-only: rtl_legacy returns mixed symbols (SPY/QQQ/NVDA contaminated into ALB etc.)
            // which causes massive OI/Volume mismatches vs the data table.
            const urls = [
                getApiUrl(
                    `/api/accumulated-data?source=stable&underlyings=${encodeURIComponent(symbol)}&${gexQs}`,
                ),
            ];
            for (const url of urls) {
                try {
                    const json = await fetchStableAccumulatedJson(url);
                    rawRows = json.data || [];
                    if (rawRows.length) break;
                } catch {
                    // try next source
                }
            }
            const rows = applyProfileMode(rawRows, profileMode);
            if (!rows.length) {
                setLoading(false);
                return;
            }

            const underlyingPrice = getUnderlyingPriceFallback(
                rows,
                "accumulated" as any,
            );
            const strikeMap = new Map<
                number,
                {
                    gex: number;
                    spotGamma: number;
                    callGex: number;
                    putGex: number;
                    oi: number;
                }
            >();

            for (const row of rows) {
                const strike = Number(
                    row.Strike ?? row["Strike Price"] ?? row.strike ?? 0,
                );
                if (!strike || strike <= 0) continue;

                const result = computeSpotGammaGexMme(
                    row,
                    "accumulated" as any,
                    underlyingPrice,
                );
                const isCall = result.direction > 0;

                const existing = strikeMap.get(strike) || {
                    gex: 0,
                    spotGamma: 0,
                    callGex: 0,
                    putGex: 0,
                    oi: 0,
                };
                existing.gex += result.gex;
                existing.spotGamma += result.spotGamma;
                if (isCall) existing.callGex += result.gex;
                else existing.putGex += result.gex;
                existing.oi += result.openInterest;
                strikeMap.set(strike, existing);
            }

            const bars: GammaBar[] = [];
            strikeMap.forEach((val, strike) => {
                bars.push({ strike, ...val });
            });
            bars.sort((a, b) => a.strike - b.strike);
            const maxAbsGex = bars.reduce(
                (m, b) => Math.max(m, Math.abs(b.gex)),
                0,
            );
            const barsWithStructure: GammaBar[] = bars.map((b) => ({
                ...b,
                gexStructure: classifyGexStructure(b.gex, maxAbsGex),
            }));
            setGammaData(barsWithStructure);

            const now = Date.now();
            const gexByStrike = new Map<number, number>();
            for (const b of barsWithStructure) gexByStrike.set(b.strike, b.gex);
            historyRef.current.push({ ts: now, gexByStrike });
            historyRef.current = historyRef.current.filter(
                (s) => now - s.ts <= 2 * 60 * 60 * 1000,
            );

            const s1 = nearestSnapshot(historyRef.current, now - 1 * 60 * 1000);
            const s5 = nearestSnapshot(historyRef.current, now - 5 * 60 * 1000);
            const s15 = nearestSnapshot(
                historyRef.current,
                now - 15 * 60 * 1000,
            );
            const s30 = nearestSnapshot(
                historyRef.current,
                now - 30 * 60 * 1000,
            );

            const lb: GammaLookbackByStrike = {};
            for (const b of barsWithStructure) {
                const k = String(b.strike);
                lb[k] = {
                    m1: s1?.gexByStrike.get(b.strike) ?? b.gex,
                    m5: s5?.gexByStrike.get(b.strike) ?? b.gex,
                    m15: s15?.gexByStrike.get(b.strike) ?? b.gex,
                    m30: s30?.gexByStrike.get(b.strike) ?? b.gex,
                };
            }
            setLookbackByStrike(lb);

            // Compute levels (same gamma typing as Hybrid / First Glance)
            const gammaStrikeData = barsWithStructure.map((b) => ({
                strike: b.strike,
                spotGamma: b.spotGamma,
                gammaExposure: b.gex,
                gammaType: classifyGammaTypeFromGexAndSpot(
                    b.spotGamma,
                    b.gex,
                    maxAbsGex,
                ),
                optionDominance:
                    b.callGex > Math.abs(b.putGex)
                        ? ("call_sold" as const)
                        : ("put_sold" as const),
            }));

            try {
                const rawLevels = computeGammaLevels(
                    gammaStrikeData,
                    underlyingPrice,
                );
                const levels: GammaLevel[] = rawLevels
                    .map((l: any) => ({
                        strike: l.strike ?? l.price ?? 0,
                        label: l.label || l.type || "",
                        type: mapLevelType(l.type || l.label || ""),
                    }))
                    .filter((l: GammaLevel) => l.strike > 0);
                setGammaLevels(levels);
            } catch {
                // compute levels from data directly
                if (barsWithStructure.length) {
                    const maxGex = barsWithStructure.reduce(
                        (best, b) => (b.gex > best.gex ? b : best),
                        barsWithStructure[0],
                    );
                    const callWall = barsWithStructure
                        .filter((b) => b.callGex > 0)
                        .reduce(
                            (best, b) => (b.callGex > best.callGex ? b : best),
                            barsWithStructure[0],
                        );
                    const putWall = barsWithStructure
                        .filter((b) => b.putGex < 0)
                        .reduce(
                            (best, b) => (b.putGex < best.putGex ? b : best),
                            barsWithStructure[0],
                        );
                    const levels: GammaLevel[] = [];
                    if (maxGex)
                        levels.push({
                            strike: maxGex.strike,
                            label: "Max GEX",
                            type: "max_gamma",
                        });
                    if (callWall && callWall.strike !== maxGex?.strike)
                        levels.push({
                            strike: callWall.strike,
                            label: "Call Wall",
                            type: "call_wall",
                        });
                    if (putWall && putWall.strike !== maxGex?.strike)
                        levels.push({
                            strike: putWall.strike,
                            label: "Put Wall",
                            type: "put_wall",
                        });
                    // Gamma flip
                    const sorted = [...barsWithStructure].sort(
                        (a, b) => a.strike - b.strike,
                    );
                    let cum = 0;
                    for (const b of sorted) {
                        const prev = cum;
                        cum += b.gex;
                        if ((prev <= 0 && cum > 0) || (prev >= 0 && cum < 0)) {
                            levels.push({
                                strike: b.strike,
                                label: "Flip",
                                type: "gamma_flip",
                            });
                            break;
                        }
                    }
                    setGammaLevels(levels);
                }
            }

            setMetrics({
                updatedAt: now,
                spot: underlyingPrice || 0,
                zeroGamma: computeZeroGammaStrike(barsWithStructure),
                majorPositive: computeMajorPositiveStrike(barsWithStructure),
                majorNegative: computeMajorNegativeStrike(barsWithStructure),
                netGex: barsWithStructure.reduce((acc, b) => acc + b.gex, 0),
                maxChange1m: computeMaxChangeStrike(barsWithStructure, s1),
                maxChange5m: computeMaxChangeStrike(barsWithStructure, s5),
                maxChange15m: computeMaxChangeStrike(barsWithStructure, s15),
                maxChange30m: computeMaxChangeStrike(barsWithStructure, s30),
                nearestRepulsiveStrike: nearestStructureStrikeToSpot(
                    barsWithStructure,
                    underlyingPrice,
                    "repulsive",
                ),
                nearestMagneticStrike: nearestStructureStrikeToSpot(
                    barsWithStructure,
                    underlyingPrice,
                    "magnetic",
                ),
            });
        } catch {
            // silent
        } finally {
            inFlightKeysRef.current.delete(key);
            setLoading(false);
        }
    }, [symbol, profileMode]);

    useEffect(() => {
        fetch_();
        timerRef.current = setInterval(fetch_, REFRESH_MS);
        return () => clearInterval(timerRef.current);
    }, [fetch_]);

    return { gammaData, gammaLevels, lookbackByStrike, metrics, loading };
}

function nearestStructureStrikeToSpot(
    bars: GammaBar[],
    spot: number,
    kind: "repulsive" | "magnetic",
): number | null {
    const cand = bars.filter((b) => b.gexStructure === kind);
    if (!cand.length) return null;
    const s = spot || 0;
    let best = cand[0];
    let bestD = Math.abs(best.strike - s);
    for (let i = 1; i < cand.length; i++) {
        const b = cand[i];
        const d = Math.abs(b.strike - s);
        if (
            d < bestD ||
            (d === bestD && Math.abs(b.gex) > Math.abs(best.gex))
        ) {
            best = b;
            bestD = d;
        }
    }
    return best.strike;
}

function mapLevelType(raw: string): GammaLevel["type"] {
    const l = raw.toLowerCase();
    if (l.includes("call") && l.includes("wall")) return "call_wall";
    if (l.includes("put") && l.includes("wall")) return "put_wall";
    if (l.includes("flip")) return "gamma_flip";
    if (l.includes("max")) return "max_gamma";
    return "key_strike";
}

function applyProfileMode(rows: any[], mode: GEXProfileMode): any[] {
    if (!rows.length || mode === "full") return rows;
    const now = Date.now();
    const withExpiry = rows
        .map((r) => {
            const rawExp = String(
                r["Expiry Date"] ?? r.expiry_date ?? r.expiry ?? "",
            ).slice(0, 10);
            const ts = rawExp ? Date.parse(rawExp) : NaN;
            return { r, ts };
        })
        .filter(
            (x) => Number.isFinite(x.ts) && x.ts >= now - 24 * 60 * 60 * 1000,
        );
    if (!withExpiry.length) return rows;
    const uniq = Array.from(new Set(withExpiry.map((x) => x.ts))).sort(
        (a, b) => a - b,
    );
    const target = mode === "latest" ? uniq[0] : (uniq[1] ?? uniq[0]);
    return withExpiry.filter((x) => x.ts === target).map((x) => x.r);
}

function nearestSnapshot(
    history: Snapshot[],
    targetTs: number,
): Snapshot | null {
    if (!history.length) return null;
    let chosen: Snapshot | null = null;
    for (const s of history) {
        if (s.ts <= targetTs) chosen = s;
    }
    return chosen ?? history[0];
}

function computeZeroGammaStrike(bars: GammaBar[]): number | null {
    if (!bars.length) return null;
    const sorted = [...bars].sort((a, b) => a.strike - b.strike);
    let cum = 0;
    for (const b of sorted) {
        const prev = cum;
        cum += b.gex;
        if ((prev <= 0 && cum >= 0) || (prev >= 0 && cum <= 0)) return b.strike;
    }
    return null;
}

function computeMajorPositiveStrike(bars: GammaBar[]): number | null {
    const pos = bars.filter((b) => b.gex > 0);
    if (!pos.length) return null;
    return pos.reduce((best, b) => (b.gex > best.gex ? b : best), pos[0])
        .strike;
}

function computeMajorNegativeStrike(bars: GammaBar[]): number | null {
    const neg = bars.filter((b) => b.gex < 0);
    if (!neg.length) return null;
    return neg.reduce((best, b) => (b.gex < best.gex ? b : best), neg[0])
        .strike;
}

function computeMaxChangeStrike(
    bars: GammaBar[],
    snap: Snapshot | null,
): number | null {
    if (!bars.length || !snap) return null;
    let bestStrike: number | null = null;
    let bestAbsDelta = -1;
    for (const b of bars) {
        const prev = snap.gexByStrike.get(b.strike) ?? b.gex;
        const absDelta = Math.abs(b.gex - prev);
        if (absDelta > bestAbsDelta) {
            bestAbsDelta = absDelta;
            bestStrike = b.strike;
        }
    }
    return bestStrike;
}
